import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AssetScanner, { canScan } from './AssetScanner';
import AssetForm, { labelsFor } from './AssetForm';
import { useConfirm } from './ConfirmDialog';
import { prefillFromCode, refreshAssetMeta, tagText, useAssetMeta } from '../lib/assets';
import './StockTake.css';
import { cameFrom } from './asset/BackLink';

/**
 * The team stock take, in Lookup (Gordon, 29 October 2026).
 *
 * "A quick stock take using the Lookup tool whereby 1 or more staff can
 * actively scan devices and these are recorded against the list of assets.
 * Needs to be intelligent enough to be done by room."
 *
 * StockTakeHub   the counts open now (Join), and starting one on any place -
 *                picked from the list or by scanning the room's label. A
 *                place already inside an open count joins that count instead.
 * StockTakeCount the counting screen: where I am standing ("I'm in"), the
 *                scanner (camera on a phone, the box for a USB scanner),
 *                what each scan was, the progress room by room, who is
 *                counting where, and finishing. It asks the server again
 *                every five seconds, so everybody sees everybody's scans.
 *
 * WHERE I AM picks itself: with no room chosen, the first device found that
 * belongs in a room puts me in that room. A device that belongs in another
 * room says so, with "I'm in Room 102" to switch and count it there. A room
 * or shelf label sets it too.
 *
 * Every code Lookup reads works here (a label, a tag, a serial number, a MAC
 * address, a hostname); the server decides what it is. API: see
 * backend/routes/asset-stocktake.js (migration 112 for the team parts).
 *
 * A code on the "Not in the register" list (and in a finished count's
 * "Codes not in the register") has "Add" for somebody who may add devices:
 * the add form, started with the code as Lookup does it, and Kept at the
 * place being counted - where it was scanned, else the count's place - which
 * can be changed before saving (Gordon, 4 November).
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ST = `${API_BASE}/assets/stocktakes`;
const PLACE_CODE = /\/assets\/place\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?\s*$/i;
const POLL_MS = 5000;
const HERE_MS = 10 * 60 * 1000;

/** A phone, or anything held rather than sat at: the camera opens by itself there. */
const handheld = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches;

const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '');

/** "40 minutes ago", for when a device last reported. */
function ago(iso) {
  const t = new Date(iso).getTime();
  if (!iso || Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 2) return 'just now';
  if (mins < 120) return `${mins} minutes ago`;
  return `${Math.round(mins / 60)} hours ago`;
}
const recently = (iso) => Boolean(iso) && Date.now() - new Date(iso).getTime() < 24 * 3600 * 1000;
const isHere = (p) => Boolean(p.active_at) && Date.now() - new Date(p.active_at).getTime() < HERE_MS;

/** Hints for a device nobody has scanned yet: who has it, and whether it reported in today. */
function hints(d) {
  const out = [];
  if (d.holder_name) out.push(`with ${d.holder_name}`);
  if (recently(d.last_seen_at)) out.push(`online ${ago(d.last_seen_at)}`);
  return out;
}

/* ------------------------------------------------------------------ hub */

export function StockTakeHub({ placeId, onOpen }) {
  const [open, setOpen] = useState(null);
  const [places, setPlaces] = useState([]);
  const [chosen, setChosen] = useState(placeId || '');
  const [past, setPast] = useState(null);
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [contains, setContains] = useState(null);

  const join = useCallback(async (id, at, name) => {
    try { await axios.post(`${ST}/${id}/me`, { place_id: at || null }); } catch { /* before 112: counting still works */ }
    onOpen(id, name ? `Joined the count of ${name}.` : null);
  }, [onOpen]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [o, m] = await Promise.all([
          axios.get(`${ST}/open`).catch(() => ({ data: { stocktakes: [] } })),
          axios.get(`${API_BASE}/assets/meta`).catch(() => ({ data: {} }))
        ]);
        if (!live) return;
        setOpen(o.data.stocktakes || []);
        setPlaces([...(m.data.locations || [])].sort((a, b) => String(a.path || a.name).localeCompare(String(b.path || b.name))));
      } catch { if (live) setOpen([]); }
    })();
    return () => { live = false; };
  }, []);

  // From a place label or the old stocktake address: join the count that
  // covers this place, or offer to start one here.
  useEffect(() => {
    if (!placeId) return undefined;
    let live = true;
    setChosen(placeId);
    (async () => {
      try {
        const { data } = await axios.get(`${ST}/covering`, { params: { location_id: placeId } });
        if (!live) return;
        if (data.open) { await join(data.open.id, placeId, data.open.place_name); return; }
        setPast((data.past || [])[0] || null);
      } catch (e) {
        if (live) setError(e.response?.data?.error || 'Could not look for a count of that place.');
      }
    })();
    return () => { live = false; };
  }, [placeId, join]);

  const nameOf = (id) => places.find((p) => p.id === id)?.path || places.find((p) => p.id === id)?.name || 'that place';

  const start = async () => {
    if (!chosen) return;
    setBusy(true);
    setError('');
    setContains(null);
    try {
      const { data } = await axios.post(ST, { location_id: chosen });
      await join(data.stocktake.id, null, null);
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.id && (d.relation === 'same' || d.relation === 'inside')) {
        await join(d.id, chosen, d.place_name || nameOf(d.location_id));
      } else if (e.response?.status === 409 && d?.id) {
        setContains({ id: d.id, name: d.place_name });
        setError(d.error || 'A count is already open inside this place.');
      } else {
        setError(d?.error || 'Could not start the stock take.');
      }
    } finally { setBusy(false); }
  };

  const pickCode = (raw) => {
    const m = PLACE_CODE.exec(String(raw || '').trim());
    const id = m && m[1].toLowerCase();
    if (id && places.some((p) => p.id === id)) { setChosen(id); setError(''); return; }
    setError(id ? 'That label is for a place you cannot count.' : 'That is not a place label. Scan the label on the room, cupboard or shelf.');
  };
  const onRaw = useCallback((raw) => { setScanning(false); pickCode(raw); return true; }, [places]); // eslint-disable-line react-hooks/exhaustive-deps
  const noop = useCallback(() => {}, []);

  return (
    <div className="st-hub">
      {error && (
        <p className="as-error" role="alert">
          {error}{contains && <> <Link to={`/assets/lookup?stocktake=${contains.id}`}>Open the count of {contains.name || 'it'}</Link></>}
        </p>
      )}

      <section className="st-open" data-testid="st-open">
        <h2>Counts going on now</h2>
        {open === null && <p className="as-quiet">Loading…</p>}
        {open && !open.length && <p className="as-quiet">Nobody is counting anything at the moment.</p>}
        {open && open.length > 0 && (
          <ul>
            {open.map((s) => (
              <li key={s.id} data-testid="st-open-row">
                <div>
                  <b>{s.place_name}</b>
                  <div className="as-quiet">
                    {s.path}{s.path ? ' · ' : ''}started by {s.started_by_name || 'somebody'} · {s.people || 0} counting
                    {' · '}{(s.counts?.found || 0) + (s.counts?.misplaced || 0)} of {s.counts?.expected || 0}
                  </div>
                </div>
                <button type="button" className="btn btn-primary btn-small" onClick={() => join(s.id, null, null)}>Join</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="st-start" data-testid="st-start">
        <h2>Start a stock take</h2>
        <p className="as-quiet">Any place can be counted: a floor, a room, a desk, a store or a shelf. Everything beneath it is counted with it.</p>
        <div className="form-group">
          <label htmlFor="st-start-place">Place</label>
          <select id="st-start-place" value={chosen} onChange={(e) => setChosen(e.target.value)}>
            <option value="">Choose a place…</option>
            {places.map((p) => <option key={p.id} value={p.id}>{p.path || p.name}</option>)}
          </select>
        </div>
        <p className="as-quiet">or scan the room’s label:</p>
        {scanning && canScan()
          ? <AssetScanner onTag={noop} onRaw={onRaw} onCancel={() => setScanning(false)} />
          : canScan() && (
            <button type="button" className="btn btn-secondary st-wide" onClick={() => setScanning(true)}>Scan a place label</button>
          )}
        <form className="al-form" onSubmit={(e) => { e.preventDefault(); pickCode(code); setCode(''); }}>
          <label htmlFor="st-start-code" className="al-sr">Place label</label>
          <input id="st-start-code" type="text" value={code} autoComplete="off" autoCapitalize="none" spellCheck={false}
            placeholder="Scan the label, then Enter" onChange={(e) => setCode(e.target.value)} />
          <button type="submit" className="btn btn-secondary" disabled={!code.trim()}>Use</button>
        </form>
        {past?.summary && (
          <p className="as-quiet" data-testid="st-past">
            Last counted {day(past.finished_at)}: {past.summary.found} found, {past.summary.misplaced} misplaced,
            {' '}{past.summary.extra} extra, {past.summary.missing} missing.
          </p>
        )}
        <button type="button" className="btn btn-primary st-big" onClick={start} disabled={busy || !chosen}>
          Start counting{chosen ? ` ${places.find((p) => p.id === chosen)?.name || ''}` : ''}
        </button>
      </section>
    </div>
  );
}

/* --------------------------------------------------------------- invite */

function Invite({ id }) {
  const [svg, setSvg] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/assets/lookup?stocktake=${id}`;
  useEffect(() => {
    let live = true;
    // The QR is drawn by the server (it has the qrcode package); without it
    // the link alone is offered.
    axios.get(`${ST}/${id}/invite`, { params: { base: window.location.origin } })
      .then(({ data }) => { if (live && /^\s*<svg[\s>]/i.test(String(data?.svg || ''))) setSvg(data.svg); })
      .catch(() => {});
    return () => { live = false; };
  }, [id]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setCopied(false); }
  };
  return (
    <div className="st-invite" data-testid="st-invite">
      {svg && <div className="st-qr" aria-label="QR code to join this count" role="img" dangerouslySetInnerHTML={{ __html: svg }} />}
      <p>Colleagues scan this with their phone, or open the link, to count with you.</p>
      <p className="st-link">{url}</p>
      <button type="button" className="btn btn-secondary btn-small" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
    </div>
  );
}

/* ------------------------------------------------------------- counting */

export function StockTakeCount({ id, notice }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(notice ? { result: 'note', text: notice } : null);
  const [myPlace, setMyPlace] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [inviting, setInviting] = useState(false);
  const [scanning, setScanning] = useState(() => canScan() && handheld());
  const [moveMisplaced, setMoveMisplaced] = useState(false);
  const [extraTo, setExtraTo] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [lost, setLost] = useState({});
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const canAdd = Boolean(meta?.access?.write);
  const [adding, setAdding] = useState(null);   // { code, place }: the unknown code being added, and where it was counted
  const input = useRef(null);
  const placeSelect = useRef(null);
  const placeKnown = useRef(false);
  const joined = useRef(false);
  const keepScanning = useRef(scanning);
  const again = useRef(null);

  const refocus = useCallback(() => { setTimeout(() => { if (!keepScanning.current) input.current?.focus(); }, 0); }, []);

  const load = useCallback(async () => {
    const r = await axios.get(`${ST}/${id}`);
    setData(r.data);
    if (!placeKnown.current) { placeKnown.current = true; setMyPlace(r.data.me?.place_id || null); }
    return r.data;
  }, [id]);

  useEffect(() => {
    let live = true;
    placeKnown.current = false;
    joined.current = false;
    (async () => {
      try {
        const d = await load();
        if (!live) return;
        // Opened from an invite: join, so the others see me.
        if (d.team && !d.stocktake.finished_at && !(d.people || []).some((p) => p.me) && !joined.current) {
          joined.current = true;
          axios.post(`${ST}/${id}/me`, { place_id: null }).catch(() => {});
        }
      } catch (e) {
        if (live) setError(e.response?.data?.error || 'Could not open the stock take.');
      }
    })();
    return () => { live = false; };
  }, [id, load]);

  // Everybody's scans, every five seconds while the page is in view.
  const finished = Boolean(data?.stocktake?.finished_at);
  useEffect(() => {
    if (finished) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load, finished]);

  useEffect(() => () => clearTimeout(again.current), []);
  useEffect(() => { if (!scanning) input.current?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const root = data?.place;
  const places = useMemo(() => (data ? data.places || data.shelves || [] : []), [data]);
  const nameOf = useCallback((pid) => {
    if (!pid) return '';
    if (root && pid === root.id) return root.name;
    return places.find((p) => p.id === pid)?.name || '';
  }, [places, root]);
  const inScope = useCallback((pid) => Boolean(pid) && ((root && pid === root.id) || places.some((p) => p.id === pid)), [places, root]);

  const setPlace = useCallback(async (pid) => {
    setMyPlace(pid || null);
    try { await axios.post(`${ST}/${id}/me`, { place_id: pid || null }); } catch (e) {
      if (e.response?.status === 400) setError(e.response.data?.error || 'That place is not part of this count.');
    }
  }, [id]);

  const stopScanning = useCallback(() => { keepScanning.current = false; clearTimeout(again.current); setScanning(false); refocus(); }, [refocus]);
  const startScanning = useCallback(() => { keepScanning.current = true; setScanning(true); }, []);
  // After each read the camera opens again by itself, until Stop scanning.
  const reopen = useCallback(() => {
    setScanning(false);
    clearTimeout(again.current);
    if (keepScanning.current) again.current = setTimeout(() => { if (keepScanning.current) setScanning(true); }, 1100);
  }, []);

  const scan = useCallback(async (body, at = myPlace) => {
    if (!data || finished) return;
    setBusy(true);
    setError('');
    try {
      const { data: r } = await axios.post(`${ST}/${id}/scan`, { ...body, place_id: at || undefined });
      if (r.result === 'place') {
        if (r.in_scope) { setMyPlace(r.place.id); setLast({ result: 'place', place: r.place }); } else setLast({ result: 'elsewhere' });
      } else if (r.result === 'choose') {
        setLast({ result: 'choose', devices: r.devices || [] });
      } else {
        let moved = null;
        if (r.result === 'found' && !at && r.suggest_place_id && inScope(r.suggest_place_id)) {
          moved = r.suggest_place_id;
          await setPlace(moved);
          // Recorded again where it was found, so "already counted … in
          // Room 101" can say where (the same person re-scanning re-records).
          await axios.post(`${ST}/${id}/scan`, { asset_id: r.device.asset_id, place_id: moved }).catch(() => {});
        }
        setLast({ ...r, at: at || moved || null, autoPlace: moved });
      }
      await load().catch(() => {});
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 404 && d?.result === 'unknown') {
        setLast({ result: 'unknown', code: d.code || body.code, recorded: d.recorded });
        await load().catch(() => {});
      } else if (e.response?.status === 404) {
        setLast({ result: 'unknown', code: body.code, recorded: false });
      } else {
        setError(d?.error || 'Could not record that scan.');
      }
    } finally {
      setBusy(false);
      setCode('');
      refocus();
    }
  }, [data, finished, id, myPlace, inScope, setPlace, load, refocus]);

  const handle = useCallback((raw) => {
    const text = String(raw || '').trim();
    if (!text) return;
    const m = PLACE_CODE.exec(text);
    if (m && inScope(m[1].toLowerCase())) {
      const pid = m[1].toLowerCase();
      setPlace(pid);
      setLast({ result: 'place', place: { id: pid, name: nameOf(pid) } });
      setCode('');
      refocus();
      return;
    }
    scan({ code: text });
  }, [inScope, nameOf, setPlace, scan, refocus]);

  const onRaw = useCallback((raw) => { reopen(); handle(raw); return true; }, [reopen, handle]);
  const noop = useCallback(() => {}, []);

  const undo = async (assetId) => {
    try {
      await axios.delete(`${ST}/${id}/scans/${assetId}`);
      setLast({ result: 'note', text: 'Undone. That scan is no longer counted.' });
      await load().catch(() => {});
    } catch (e) { setError(e.response?.data?.error || 'Could not undo that scan.'); }
  };
  const imIn = async (pid, assetId) => {
    await setPlace(pid);
    await scan({ asset_id: assetId }, pid);
  };
  const markDone = async (pid, done) => {
    try {
      await axios.post(`${ST}/${id}/places/${pid}/done`, { done });
      await load().catch(() => {});
    } catch (e) { setError(e.response?.data?.error || 'Could not mark that place.'); }
  };
  const dropUnknown = async (uid) => {
    try {
      await axios.delete(`${ST}/${id}/unknowns/${uid}`);
      await load().catch(() => {});
    } catch (e) { setError(e.response?.data?.error || 'Could not remove that code.'); }
  };

  const finish = async () => {
    const missing = data?.counts?.missing ?? 0;
    const open = places.filter((p) => !p.done).length;
    const parts = [];
    if (open) parts.push(`${open} place${open === 1 ? ' is' : 's are'} not marked done.`);
    if (missing) {
      parts.push(`${missing} expected device${missing === 1 ? ' has' : 's have'} not been scanned and will be reported as missing. Nothing is marked lost unless you choose to afterwards.`);
    }
    const ok = await confirm({
      title: 'Finish this stock take?',
      message: parts.length ? parts.join(' ') : 'Every expected device has been scanned.',
      confirmLabel: 'Finish'
    });
    if (!ok) return;
    stopScanning();
    setFinishing(true);
    setError('');
    try {
      const r = await axios.post(`${ST}/${id}/finish`, { move_misplaced: moveMisplaced, move_extra_to: extraTo || null });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not finish the stock take.');
    } finally { setFinishing(false); }
  };

  const markLost = async (d) => {
    setLost((l) => ({ ...l, [d.asset_id]: 'busy' }));
    try {
      await axios.post(`${API_BASE}/assets/${d.asset_id}/lost`, {
        reason: `Not found in the stock take of ${root?.name || 'this place'} on ${day(data.stocktake.finished_at || new Date().toISOString())}`
      });
      setLost((l) => ({ ...l, [d.asset_id]: 'done' }));
    } catch (e) {
      if (e.response?.status === 409) setLost((l) => ({ ...l, [d.asset_id]: 'already' }));
      else { setLost((l) => ({ ...l, [d.asset_id]: null })); setError(e.response?.data?.error || 'Could not mark it lost.'); }
    }
  };

  if (error && !data) return <p className="as-error" role="alert">{error}</p>;
  if (!data) return <p className="as-quiet">Loading…</p>;

  const { stocktake, counts = {}, expected = [], extra = [], unknowns = [], people = [], recent = [] } = data;
  const summary = stocktake.summary || {};
  const rows = [
    { id: root.id, name: root.name, kind: root.kind, depth: 0, root: true, done: root.done || null },
    ...places
  ].map((p) => ({ ...p, items: expected.filter((e) => e.location_id === p.id) }))
    .filter((p) => !p.root || p.items.length);
  const here = (pid) => people.filter((p) => isHere(p) && p.place_id === pid);
  const writer = data.can_write !== false;

  const device = (d) => (d ? [tagText(d), [d.manufacturer, d.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') : '');
  const banner = () => {
    if (!last) return null;
    const d = last.device;
    const undoBtn = d && ['found', 'misplaced', 'extra'].includes(last.result) && (
      <button type="button" className="btn btn-plain btn-small" onClick={() => undo(d.asset_id)}>Undo</button>
    );
    let body;
    if (last.result === 'found') {
      body = <>✓ Found: {device(d)}{last.at ? ` in ${nameOf(last.at)}` : ''}</>;
    } else if (last.result === 'misplaced') {
      const where = nameOf(last.suggest_place_id) || d.location_name;
      body = (
        <>Misplaced: {device(d)} belongs in {where || 'another place'}.
          {last.suggest_place_id && inScope(last.suggest_place_id) && (
            <> <button type="button" className="btn btn-secondary btn-small" onClick={() => imIn(last.suggest_place_id, d.asset_id)}>
              I’m in {nameOf(last.suggest_place_id)}
            </button></>
          )}
        </>
      );
    } else if (last.result === 'extra') {
      body = <>Extra: {device(d)} is registered at {d.location_path || d.location_name || 'no place'}.</>;
    } else if (last.result === 'already') {
      body = <>Already counted: {device(d)} by {last.by_name || 'somebody'}{last.place_id ? ` in ${nameOf(last.place_id)}` : ''} at {clock(last.scanned_at)}.</>;
    } else if (last.result === 'unknown') {
      body = <>Unknown: {last.code} is not in the register{last.recorded ? ' — kept on the unknown list' : ''}.</>;
    } else if (last.result === 'choose') {
      body = (
        <>Several devices match. Which one is it?
          <span className="st-choices">
            {last.devices.map((c) => (
              <button key={c.asset_id} type="button" className="btn btn-secondary btn-small" onClick={() => scan({ asset_id: c.asset_id })}>
                {[tagText(c), c.model, c.serial_number, c.location_name].filter(Boolean).join(' · ')}
              </button>
            ))}
          </span>
        </>
      );
    } else if (last.result === 'place') {
      body = <>Now counting in {last.place?.name || nameOf(last.place?.id)}.</>;
    } else if (last.result === 'elsewhere') {
      body = <>That label is for a place outside this count.</>;
    } else {
      body = <>{last.text}</>;
    }
    return (
      <div className="st-banner" data-testid="st-banner" data-result={last.result} role="status">
        <span>{body}</span>
        {last.autoPlace && (
          <span className="st-auto">Counting in {nameOf(last.autoPlace)}.{' '}
            <button type="button" className="btn btn-plain btn-small" onClick={() => placeSelect.current?.focus()}>Change</button>
          </span>
        )}
        {undoBtn}
      </div>
    );
  };

  const progress = (items) => items.filter((i) => i.scan).length;

  return (
    <div className="st-count" data-testid="st-count">
      <header className="st-head">
        <div>
          <h2 className="st-title">Counting {root.name}</h2>
          <p className="as-quiet">
            {root.path}{root.path ? ' · ' : ''}started {day(stocktake.started_at)} {clock(stocktake.started_at)}
            {stocktake.started_by_name ? ` by ${stocktake.started_by_name}` : ''}
          </p>
        </div>
        {!finished && (
          <button type="button" className="btn btn-secondary btn-small" aria-expanded={inviting} onClick={() => setInviting((v) => !v)}>
            Invite
          </button>
        )}
      </header>
      {inviting && !finished && <Invite id={id} />}
      {people.length > 0 && (
        <ul className="st-people" data-testid="st-people" aria-label="Counting">
          {people.map((p) => (
            <li key={p.user_id} data-here={isHere(p)} data-me={Boolean(p.me)}>
              <span className="st-dot" aria-hidden="true" />
              {p.me ? 'You' : p.name}{p.place_id ? ` · ${nameOf(p.place_id)}` : ''}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="as-error" role="alert">{error}</p>}

      <div className="as-st-counts" data-testid="st-counts">
        <span><b>{counts.expected ?? 0}</b> expected</span>
        <span><b>{counts.found ?? 0}</b> found</span>
        <span><b>{counts.misplaced ?? 0}</b> misplaced</span>
        <span><b>{counts.extra ?? 0}</b> extra</span>
        <span><b>{counts.missing ?? 0}</b> not yet seen</span>
        {(counts.unknown ?? 0) > 0 && <span><b>{counts.unknown}</b> unknown</span>}
      </div>

      {!finished && writer && (
        <section className="st-scan">
          <div className="form-group">
            <label htmlFor="st-place">I’m in</label>
            <select id="st-place" ref={placeSelect} value={myPlace || ''} onChange={(e) => { setPlace(e.target.value); refocus(); }}>
              <option value="">Not chosen (the first device I find picks the room)</option>
              <option value={root.id}>{root.name} (the whole place)</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>{`${'  '.repeat(Math.max(0, (p.depth || 1) - 1))}${p.name}`}</option>
              ))}
            </select>
          </div>
          {scanning && canScan()
            ? <AssetScanner barcodes onTag={noop} onRaw={onRaw} onCancel={stopScanning} />
            : canScan() && (
              <button type="button" className="btn btn-primary st-big" onClick={startScanning}>Scan with the camera</button>
            )}
          <form className="al-form" onSubmit={(e) => { e.preventDefault(); handle(code); }}>
            <label htmlFor="st-code" className="al-sr">Code</label>
            <input id="st-code" ref={input} type="text" value={code} autoComplete="off" autoCapitalize="none"
              spellCheck={false} placeholder="Scan or type a code, then Enter" onChange={(e) => setCode(e.target.value)} />
            <button type="submit" className="btn btn-secondary" disabled={busy || !code.trim()}>Count</button>
          </form>
          {banner()}
        </section>
      )}

      {finished && (
        <section className="as-st-summary" data-testid="st-summary">
          <h2>Finished {day(stocktake.finished_at)}{stocktake.finished_by_name ? ` by ${stocktake.finished_by_name}` : ''}</h2>
          <p>
            {summary.found ?? 0} found, {summary.misplaced ?? 0} misplaced, {summary.extra ?? 0} extra, {summary.missing ?? 0} missing.
            {summary.moved_misplaced ? ' Misplaced devices were moved to where they were scanned.' : ''}
            {summary.moved_extra_to ? ` Extra devices were put in ${nameOf(summary.moved_extra_to) || 'the chosen place'}.` : ''}
          </p>
          {(summary.missing_devices || []).length > 0 ? (
            <>
              <h3>Missing</h3>
              <ul className="st-missing">
                {summary.missing_devices.map((d) => (
                  <li key={d.asset_id} data-testid="st-missing">
                    <span>
                      <Link to={`/assets/${d.asset_id}`} state={cameFrom('Stock take')}><b>{tagText(d)}</b></Link> {[d.manufacturer, d.model].filter(Boolean).join(' ')}
                      <span className="as-quiet">{[d.location_name, ...hints(d)].filter(Boolean).map((h) => ` · ${h}`).join('')}</span>
                    </span>
                    {lost[d.asset_id] === 'done' || lost[d.asset_id] === 'already'
                      ? <span className="st-lost">{lost[d.asset_id] === 'done' ? 'Marked lost' : 'Already marked lost'}</span>
                      : writer && (
                        <button type="button" className="btn btn-secondary btn-small" disabled={lost[d.asset_id] === 'busy'} onClick={() => markLost(d)}>
                          Mark lost
                        </button>
                      )}
                  </li>
                ))}
              </ul>
            </>
          ) : (summary.missing_tags || []).length > 0 && (
            <>
              <h3>Missing</h3>
              <ul className="st-missing">{summary.missing_tags.map((t) => <li key={t}>{t}</li>)}</ul>
            </>
          )}
          {(summary.unknown_codes || []).length > 0 && (
            <>
              <h3>Codes not in the register</h3>
              <ul className="st-missing">
                {summary.unknown_codes.map((c) => (
                  <li key={c}>
                    {c}
                    {canAdd && (
                      <>
                        {' '}
                        <button type="button" className="btn btn-plain btn-small" data-action="add" aria-label={`Add ${c} to the register`}
                          onClick={() => setAdding({ code: c, place: root?.id })}>Add</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {(summary.counted_by || []).length > 0 && <p className="as-quiet">Counted by {summary.counted_by.join(', ')}.</p>}
          <p><Link to="/assets/lookup?mode=stock">Back to stock takes</Link></p>
        </section>
      )}

      {!finished && (
        <section className="st-places" data-testid="st-places">
          <h2>By place</h2>
          <ul>
            {rows.map((p) => {
              const isOpen = Boolean(openRows[p.id]);
              const there = here(p.id);
              return (
                <li key={p.id} className="st-row" data-testid="st-row" data-place={p.id} data-done={Boolean(p.done)}
                  style={{ marginLeft: `${Math.max(0, (p.depth || 0) - 1) * 0.9}rem` }}>
                  <div className="st-row-head">
                    <button type="button" className="st-row-toggle" aria-expanded={isOpen}
                      onClick={() => setOpenRows((o) => ({ ...o, [p.id]: !o[p.id] }))}>
                      <span className="st-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                      <span className="st-row-name">{p.name}</span>
                      <span className="st-row-count">{progress(p.items)} of {p.items.length}</span>
                    </button>
                    {p.done && <span className="st-done">Done ✓{p.done.by_name ? ` by ${p.done.by_name}` : ''}</span>}
                    {there.length > 0 && <span className="as-quiet st-there">{there.map((x) => (x.me ? 'You' : x.name)).join(', ')}</span>}
                    {data.team && writer && (
                      <button type="button" className="btn btn-plain btn-small" onClick={() => markDone(p.id, !p.done)}>
                        {p.done ? 'Not done' : 'Mark done'}
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <ul className="st-items">
                      {p.items.map((i) => (
                        <li key={i.asset_id} data-scan={i.scan?.result || 'none'}>
                          <span className="as-st-tick" aria-hidden="true">{i.scan ? (i.scan.result === 'found' ? '✓' : '↪') : '○'}</span>
                          <span>
                            <b>{tagText(i)}</b> {[i.manufacturer, i.model].filter(Boolean).join(' ') || i.category_name}
                            {i.scan?.result === 'misplaced' && i.scan.place_id
                              ? <span className="as-quiet"> · seen in {nameOf(i.scan.place_id || i.scan.shelf_id)}</span> : null}
                            {i.scan?.scanned_by_name ? <span className="as-quiet"> · {i.scan.scanned_by_name}</span> : null}
                            {!i.scan && hints(i).length > 0 && <span className="st-hint"> · {hints(i).join(' · ')}</span>}
                          </span>
                        </li>
                      ))}
                      {!p.items.length && <li className="as-quiet">Nothing is registered here.</li>}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!finished && extra.length > 0 && (
        <section className="st-list" data-testid="st-extra">
          <h2>Extra <span className="as-quiet">{extra.length}</span></h2>
          <ul>
            {extra.map((i) => (
              <li key={i.asset_id}>
                <span className="as-st-tick" aria-hidden="true">+</span>
                <span><b>{tagText(i)}</b> {i.model}
                  <span className="as-quiet"> · registered at {i.location_path || 'no place'}{i.place_id ? ` · seen in ${nameOf(i.place_id)}` : ''}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!finished && unknowns.length > 0 && (
        <section className="st-list" data-testid="st-unknown">
          <h2>Not in the register <span className="as-quiet">{unknowns.length}</span></h2>
          <ul>
            {unknowns.map((u) => (
              <li key={u.id}>
                <span className="as-st-tick" aria-hidden="true">?</span>
                <span className="st-grow"><b>{u.code}</b>
                  <span className="as-quiet">{u.place_id ? ` · ${nameOf(u.place_id)}` : ''}{u.scanned_by_name ? ` · ${u.scanned_by_name}` : ''}</span></span>
                {canAdd && (
                  <button type="button" className="btn btn-plain btn-small" data-action="add" aria-label={`Add ${u.code} to the register`}
                    onClick={() => setAdding({ code: u.code, place: u.place_id || root?.id })}>Add</button>
                )}
                {writer && (
                  <button type="button" className="btn btn-plain btn-small" aria-label={`Remove ${u.code}`} onClick={() => dropUnknown(u.id)}>×</button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!finished && recent.length > 0 && (
        <section className="st-list" data-testid="st-recent">
          <h2>Just scanned</h2>
          <ul>
            {recent.map((r) => (
              <li key={`${r.asset_id}-${r.scanned_at}`} data-result={r.result}>
                <span className="st-grow"><b>{tagText(r)}</b> {r.model}
                  <span className="as-quiet"> · {r.result}{r.place_id ? ` · ${nameOf(r.place_id)}` : ''} · {r.scanned_by_name}</span></span>
                <span className="st-when">{clock(r.scanned_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!finished && writer && (
        <section className="as-st-finish" data-testid="st-finish">
          <h2>Finish</h2>
          <label className="as-check-inline">
            <input type="checkbox" checked={moveMisplaced} onChange={(e) => setMoveMisplaced(e.target.checked)} />
            {' '}Move misplaced devices to where they were scanned
          </label>
          <div className="form-group">
            <label htmlFor="st-extra-to">Put extra devices in</label>
            <select id="st-extra-to" value={extraTo} onChange={(e) => setExtraTo(e.target.value)}>
              <option value="">Leave them where they are registered</option>
              <option value={root.id}>{root.name}</option>
              {places.map((p) => <option key={p.id} value={p.id}>{p.path_below || p.name}</option>)}
            </select>
          </div>
          <p className="as-quiet">Devices nobody scanned are listed as missing; you can mark each one lost afterwards.</p>
          <button type="button" className="btn btn-primary st-big" onClick={finish} disabled={finishing}>Finish the stock take</button>
        </section>
      )}
      {adding && (
        <AssetForm meta={meta} prefill={{ ...prefillFromCode(adding.code), ...(adding.place ? { location_id: adding.place } : {}) }} onClose={() => { setAdding(null); refocus(); }}
          onSaved={(saved, then) => {
            setAdding(null);
            refreshAssetMeta();
            if (then === 'print') navigate(...labelsFor(saved));
            else navigate(`/assets/${saved.id}`, { state: cameFrom('Stock take') });
          }} />
      )}
      {confirmDialog}
    </div>
  );
}
